import { useEffect, useRef, useState } from 'react';

import type { ProjectDto } from '@time-tracking/shared';

import { useFocusTrap } from '../../../hooks/useFocusTrap.js';

interface EditProjectDialogProps {
  project: ProjectDto | null;
  onClose: () => void;
  onSubmit: (patch: { name: string; code: string; description: string | null }) => void;
  isPending: boolean;
}

export function EditProjectDialog({
  project,
  onClose,
  onSubmit,
  isPending,
}: EditProjectDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const open = project !== null;
  useFocusTrap(dialogRef, open);

  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');

  useEffect(() => {
    if (!project) return;
    setName(project.name);
    setCode(project.code);
    setDescription(project.description ?? '');

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [project, onClose]);

  if (!open) return null;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || !code.trim()) return;
    onSubmit({
      name: name.trim(),
      code: code.trim().toUpperCase(),
      description: description.trim() || null,
    });
  }

  return (
    <div className="dialog-backdrop">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-project-dialog-title"
        className="projects-dialog"
      >
        <h2 id="edit-project-dialog-title" className="dialog-title">
          Edit project
        </h2>
        <form onSubmit={handleSubmit}>
          <div className="dialog-form-field">
            <label htmlFor="edit-project-name">
              Project name <span aria-hidden="true">*</span>
            </label>
            <input
              id="edit-project-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              aria-required="true"
              maxLength={255}
            />
          </div>

          <div className="dialog-form-field">
            <label htmlFor="edit-project-code">
              Project code <span aria-hidden="true">*</span>
            </label>
            <input
              id="edit-project-code"
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              required
              aria-required="true"
              maxLength={64}
              style={{ fontFamily: 'var(--font-mono)' }}
            />
          </div>

          <div className="dialog-form-field">
            <label htmlFor="edit-project-description">Description</label>
            <textarea
              id="edit-project-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={2000}
              rows={3}
            />
          </div>

          <div className="dialog-footer">
            <button
              type="submit"
              className="btn btn--primary"
              disabled={!name.trim() || !code.trim() || isPending}
              aria-busy={isPending}
            >
              {isPending ? 'Saving…' : 'Save changes'}
            </button>
            <button type="button" className="btn btn--secondary" onClick={onClose}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
